import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import InlineAlert from '../../../../shared/components/InlineAlert';
import { useSaveEventDatasetTemplate } from '../../hooks/useEventDatasets';
import type { EventDatasetDefinition, EventDatasetRecord } from '../../../../types/backend';

interface DatasetSaveTemplateDialogProps {
  customerId: string;
  dataset: EventDatasetRecord;
  onClose: () => void;
  notify: (message: string, severity?: 'success' | 'error') => void;
}

// What a saved dataset carries beyond its definition; every other field, including any the definition gains, is kept.
const RECORD_ONLY_FIELDS = ['customer_id', 'created_at', 'updated_at', 'status', 'rebuild_requested_at'] as const satisfies
  readonly (keyof EventDatasetRecord)[];

/** The definition a template keeps: the dataset without its customer and its lifecycle fields. */
function definitionOf(dataset: EventDatasetRecord): EventDatasetDefinition {
  const recordOnly: readonly string[] = RECORD_ONLY_FIELDS;
  const definition = Object.fromEntries(Object.entries(dataset).filter(([field]) => !recordOnly.includes(field)));
  return definition as Omit<EventDatasetRecord, (typeof RECORD_ONLY_FIELDS)[number]>;
}

/** Saves a dataset's definition as a template this customer owns; mounted only while open. */
const DatasetSaveTemplateDialog: React.FC<DatasetSaveTemplateDialogProps> = ({ customerId, dataset, onClose, notify }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const { saveTemplate, saving, error } = useSaveEventDatasetTemplate(customerId);
  const trimmed = name.trim();

  const handleSave = () => saveTemplate(
    {
      name: trimmed,
      ...(description.trim() ? { description: description.trim() } : {}),
      definition: definitionOf(dataset),
    },
    {
      onDone: () => {
        notify('Template saved');
        onClose();
      },
      onFailed: () => undefined,
    },
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save as template</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2">
          The definition of <strong>{dataset.name}</strong> becomes a template for this customer. Datasets made from it
          are copies: a later change to the template does not reach them.
        </Typography>
        <TextField
          required
          size="small"
          label="Template name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
          InputLabelProps={{ shrink: true }}
        />
        {error && <InlineAlert severity="error">{error}</InlineAlert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!trimmed || saving}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
        >
          Save template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DatasetSaveTemplateDialog;
